/**
 * "This just changed", for a page that reads from the Postgres mirror and so sees
 * a write only once the mirror has it (about MIRROR_LAG_REFETCH_MS later).
 *
 * Writes happen all over the app, mostly through dialogs the reading page does
 * not own. Each writer calls `noteChanged`, and every page following the channel
 * answers with one catch-up refetch a mirror lag later: at once if it is open,
 * and on arrival if it opens before the lag has passed (change something, go
 * straight to the page). A list follows additions only, so that something new
 * shows up, and an edit leaves its rows where they are (out of sort or out of
 * filter) until Refresh. The dashboard counts follow every change. The channels:
 * `boardsMirrorLag.ts`, `teamsMirrorLag.ts`.
 */
import moment from 'moment-timezone';
import { MIRROR_LAG_REFETCH_MS } from '@/utils/cursorWalk';

export type MirrorLagListener = () => void;

export interface MirrorLagChannel {
	/** Something this channel follows just happened. */
	noteChanged (now?: moment.Moment): void;
	/** How long until the mirror has caught up with the latest change: 0 once it has. */
	remainingMs (now?: moment.Moment): number;
	/** Call `listener` on every change from now on. Returns the unsubscribe. */
	onChanged (listener: MirrorLagListener): () => void;
	/** For specs: forget every change and every listener. */
	reset (): void;
}

export function createMirrorLagChannel (): MirrorLagChannel {
	let lastChangedAt: moment.Moment | null = null;
	let listeners: MirrorLagListener[] = [];
	return {
		noteChanged (now: moment.Moment = moment()): void {
			lastChangedAt = now.clone();
			listeners.slice().forEach((listener: MirrorLagListener) => listener());
		},
		remainingMs (now: moment.Moment = moment()): number {
			if (!lastChangedAt) return 0;
			return Math.max(0, lastChangedAt.clone().add(MIRROR_LAG_REFETCH_MS, 'milliseconds').diff(now));
		},
		onChanged (listener: MirrorLagListener): () => void {
			listeners = [...listeners, listener];
			return () => {
				listeners = listeners.filter((l: MirrorLagListener) => l !== listener);
			};
		},
		reset (): void {
			lastChangedAt = null;
			listeners = [];
		},
	};
}
